import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Typography
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FileDrop from '../components/FileDrop';
import Panel from '../components/Panel';
import RecentFilesTable, { relativeTime } from '../components/RecentFilesTable';
import { converterConfig } from '../converters/config';

const HomePage = ({
  expiresAt,
  pendingFile,
  detectMessage,
  detectingFile,
  onSelectFile,
  historyVersion
}) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);

  useEffect(() => {
    let active = true;
    axios.get('/api/files').then(response => {
      if (active) setFiles(response.data.files || []);
    }).catch(() => {
      if (active) setFiles([]);
    });
    return () => { active = false; };
  }, [historyVersion]);

  const lastFile = type => files.find(file => file.type === type);
  const sessionTime = expiresAt
    ? new Date(expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '--:--';

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">Home</Typography>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 750 }}>
            What do you want to convert?
          </Typography>
        </Box>
        <Chip
          label={`Signed in · session ends ${sessionTime}`}
          variant="outlined"
          sx={{ bgcolor: '#fff', borderColor: 'divider', color: 'text.secondary', fontSize: 12 }}
        />
      </Box>

      <FileDrop
        file={pendingFile}
        onFileChange={onSelectFile}
        home
        disabled={detectingFile}
        message={detectingFile ? 'Checking the file type…' : ''}
      />
      {detectMessage && (
        <Typography color="error.main" variant="body2" sx={{ mt: 1 }}>
          {detectMessage}
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.75,
          mt: 2
        }}
      >
        {Object.entries(converterConfig).map(([type, config]) => {
          const Icon = config.icon;
          const previous = lastFile(type);
          return (
            <Card
              key={type}
              variant="outlined"
              sx={{ borderRadius: '14px', borderColor: 'divider', height: '100%' }}
            >
              <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '11px',
                    bgcolor: 'primary.light',
                    color: 'primary.main',
                    mb: 1.25
                  }}
                >
                  <Icon />
                </Box>
                <Typography sx={{ fontWeight: 750, mb: 0.5 }}>{config.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 22 }}>
                  {config.description}
                </Typography>
                <Box
                  sx={{
                    mt: 1.25,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '8px',
                    bgcolor: '#f5f7f8',
                    color: 'text.secondary',
                    fontSize: 12,
                    overflowWrap: 'anywhere'
                  }}
                >
                  {previous
                    ? `Last: ${previous.label} · ${relativeTime(previous.createdAt)}`
                    : 'No files yet'}
                </Box>
                <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Link href={`/api/templates/${type}`} underline="always" sx={{ fontWeight: 700, fontSize: 13 }}>
                    Template
                  </Link>
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(`/${type}`)}
                  >
                    Start
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Panel
        title="Recent files"
        action={<Typography variant="caption" color="text.secondary">Kept 24 hours</Typography>}
        sx={{ mt: 2 }}
        bodySx={{ p: 0 }}
      >
        <RecentFilesTable files={files.slice(0, 5)} />
      </Panel>
    </Box>
  );
};

export default HomePage;
